import React, { useId, useState, useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import {
    Calculator,
    ChevronDown,
    ChevronUp,
    Hash,
    Lock,
    MoreVertical,
    Pencil,
    Trash2,
    Type,
    Upload,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
export interface ConfigurationParameter {
    id: string;
    name: string;
    type: 'string' | 'integer' | 'decimal' | 'password';
}

interface ConfigurationSchemaEditorProps {
    value: ConfigurationParameter[];
    onChange: (parameters: ConfigurationParameter[]) => void;
}

const CONFIGURATION_TYPES = [
    { key: 'string', text: 'String', icon: Type },
    { key: 'integer', text: 'Integer', icon: Hash },
    { key: 'decimal', text: 'Decimal', icon: Calculator },
    { key: 'password', text: 'Password', icon: Lock },
] as const;

function generateId(): string {
    return `param-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Derives a camelCase ID from a name: first char lower, no spaces. */
function toCamelCaseId(text: string): string {
    if (!text || !text.trim()) return '';
    const words = text
        .trim()
        .replace(/[äöüß]/g, (c) => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' }[c] || c))
        .split(/[^a-zA-Z0-9]+/)
        .filter(Boolean);
    return words
        .map((word, i) => {
            const lower = word.toLowerCase();
            return i === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join('');
}

function ensureUniqueId(candidateId: string, existingIds: Set<string>): string {
    if (!existingIds.has(candidateId)) return candidateId;

    let uniqueId = `${candidateId}-${Date.now()}`;
    while (existingIds.has(uniqueId)) {
        uniqueId = `${candidateId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    }
    return uniqueId;
}

export default function ConfigurationSchemaEditor({ value, onChange }: ConfigurationSchemaEditorProps) {
    const { t } = useTranslation();
    const fieldId = useId();
    const [parameters, setParameters] = useState<ConfigurationParameter[]>(value || []);
    const [isDragging, setIsDragging] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editParameter, setEditParameter] = useState<ConfigurationParameter | null>(null);
    const [newParam, setNewParam] = useState<Partial<ConfigurationParameter>>({ type: 'string' });

    const notifyChange = useCallback(
        (updated: ConfigurationParameter[]) => {
            setParameters(updated);
            onChange(updated);
        },
        [onChange]
    );

    // Sync with external value when it changes (e.g. switching toolsets)
    React.useEffect(() => {
        setParameters(value || []);
    }, [value]);

    const handleDragStart = (e: React.DragEvent, type: ConfigurationParameter['type']) => {
        e.dataTransfer.setData('paramType', type);
        setIsDragging(true);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const type = e.dataTransfer.getData('paramType') as ConfigurationParameter['type'];
        if (!type || !CONFIGURATION_TYPES.some((t) => t.key === type)) return;

        const nextNum = parameters.filter((p) => p.type === type).length + 1;
        const paramName = `${type}-${nextNum}`;
        const existingIds = new Set(parameters.map((p) => p.id));
        const finalId = ensureUniqueId(toCamelCaseId(paramName) || generateId(), existingIds);
        const param: ConfigurationParameter = {
            id: finalId,
            name: paramName,
            type,
        };
        const updated = [...parameters, param];
        notifyChange(updated);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const addParameter = () => {
        const name = (newParam.name || '').trim();
        const type = newParam.type || 'string';
        if (!name) return;

        const idFromName = toCamelCaseId(name);
        const userProvidedId = (newParam.id || '').trim();
        const id = userProvidedId || idFromName || generateId();

        const param: ConfigurationParameter = {
            id,
            name,
            type,
        };

        const existingIds = new Set(parameters.map((p) => p.id));
        // In edit mode, keep the currently edited row out of collision detection.
        if (editParameter) {
            existingIds.delete(editParameter.id);
        }
        const finalId = ensureUniqueId(param.id, existingIds);

        let updated: ConfigurationParameter[];
        if (editParameter) {
            updated = parameters.map((p) => (p.id === editParameter.id ? { ...param, id: finalId } : p));
        } else {
            updated = [...parameters, { ...param, id: finalId }];
        }

        notifyChange(updated);
        setAddDialogOpen(false);
        setEditParameter(null);
        setNewParam({ type: 'string' });
    };

    const openEditDialog = (param: ConfigurationParameter) => {
        setEditParameter(param);
        setNewParam({ id: param.id, name: param.name, type: param.type });
        setAddDialogOpen(true);
    };

    const deleteParameter = (paramId: string) => {
        const updated = parameters.filter((p) => p.id !== paramId);
        notifyChange(updated);
    };

    const moveParameter = (index: number, direction: 'up' | 'down') => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= parameters.length) return;

        const updated = [...parameters];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
        notifyChange(updated);
    };

    const closeDialog = () => {
        setAddDialogOpen(false);
        setEditParameter(null);
        setNewParam({ type: 'string' });
    };

    return (
        <div className="min-w-0 max-w-[1000px]">
            <div className="flex flex-wrap gap-4">
                <div className="w-[180px] shrink-0 space-y-1 rounded-md border bg-muted/40 p-2">
                    <div className="mb-2 text-sm font-semibold">
                        {t('ui.configurationParameterTypes')}
                    </div>
                    {CONFIGURATION_TYPES.map((type) => {
                        const TypeIcon = type.icon;
                        return (
                            <div
                                key={type.key}
                                draggable
                                onDragStart={(e) => handleDragStart(e, type.key)}
                                className="flex cursor-grab items-center gap-2 rounded-md border bg-background px-2 py-2 text-xs"
                            >
                                <TypeIcon className="h-3.5 w-3.5" aria-hidden />
                                <span>{type.text}</span>
                            </div>
                        );
                    })}
                </div>

                <div
                    className={cn(
                        'min-h-[150px] min-w-0 flex-1 basis-1/2 rounded-md border-2 border-dashed p-3',
                        isDragging && 'bg-accent'
                    )}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragLeave={handleDragLeave}
                >
                    {parameters.length === 0 ? (
                        <div className="flex min-h-[120px] flex-col items-center justify-center text-muted-foreground">
                            <Upload className="mb-1.5 h-5 w-5" aria-hidden />
                            <div className="text-xs">{t('ui.dragConfigurationParamsHereOr')}</div>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => { setAddDialogOpen(true); setNewParam({ type: 'string' }); }}
                            >
                                {t('ui.addParameter')}
                            </Button>
                        </div>
                    ) : (
                        <div>
                            <div className="mb-4 flex items-center justify-between gap-2">
                                <div className="text-sm font-semibold">
                                    {t('ui.configurationParameters')}
                                </div>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => { setAddDialogOpen(true); setEditParameter(null); setNewParam({ type: 'string' }); }}
                                >
                                    {t('ui.addParameter')}
                                </Button>
                            </div>
                            {parameters.map((param, index) => {
                                const typeInfo = CONFIGURATION_TYPES.find((t) => t.key === param.type);
                                const TypeIcon = typeInfo?.icon || Type;
                                return (
                                    <div
                                        key={param.id}
                                        className="mb-1.5 rounded-md border bg-muted/40 p-2"
                                    >
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex min-w-0 flex-wrap items-center gap-2">
                                                <MoreVertical className="h-4 w-4 cursor-grab text-muted-foreground" aria-hidden />
                                                <TypeIcon className="h-4 w-4" aria-hidden />
                                                <span className="break-words font-semibold">{param.name}</span>
                                                <span className="break-all text-xs text-muted-foreground">({param.id})</span>
                                                <span className="text-xs text-primary">{param.type}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    disabled={index === 0}
                                                    onClick={() => moveParameter(index, 'up')}
                                                    title={t('ui.moveUp')}
                                                    aria-label={t('ui.moveUp')}
                                                >
                                                    <ChevronUp className="h-4 w-4" aria-hidden />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    disabled={index === parameters.length - 1}
                                                    onClick={() => moveParameter(index, 'down')}
                                                    title={t('ui.moveDown')}
                                                    aria-label={t('ui.moveDown')}
                                                >
                                                    <ChevronDown className="h-4 w-4" aria-hidden />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openEditDialog(param)}
                                                    title={t('ui.edit')}
                                                    aria-label={t('ui.edit')}
                                                >
                                                    <Pencil className="h-4 w-4" aria-hidden />
                                                </Button>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => deleteParameter(param.id)}
                                                    title={t('ui.delete')}
                                                    aria-label={t('ui.delete')}
                                                    className="text-destructive hover:text-destructive"
                                                >
                                                    <Trash2 className="h-4 w-4" aria-hidden />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <Dialog
                open={addDialogOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        closeDialog();
                    }
                }}
            >
                <DialogContent
                    className="max-w-[calc(100vw-2rem)] sm:max-w-[400px]"
                    onInteractOutside={(event) => event.preventDefault()}
                    onEscapeKeyDown={(event) => event.preventDefault()}
                >
                    <DialogHeader>
                        <DialogTitle>{editParameter ? t('ui.editParameter') : t('ui.addParameter')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor={`${fieldId}-name`}>
                                {t('ui.parameterName')}
                                <span aria-hidden className="ml-0.5 text-destructive">*</span>
                            </Label>
                            <Input
                                id={`${fieldId}-name`}
                                required
                                value={newParam.name || ''}
                                onChange={(event) => {
                                    const name = event.target.value || '';
                                    setNewParam((prev) => {
                                        const derivedId = toCamelCaseId(name);
                                        return {
                                            ...prev,
                                            name,
                                            id: prev.id === toCamelCaseId(prev.name || '') || !prev.id ? derivedId : prev.id,
                                        };
                                    });
                                }}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label htmlFor={`${fieldId}-id`}>{t('ui.parameterId')}</Label>
                            <Input
                                id={`${fieldId}-id`}
                                value={newParam.id || ''}
                                onChange={(event) => setNewParam({ ...newParam, id: event.target.value || '' })}
                                placeholder={t('ui.parameterIdPlaceholder')}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <div className="text-sm font-semibold">
                                {t('ui.parameterType')}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {CONFIGURATION_TYPES.map((type) => {
                                    const TypeIcon = type.icon;
                                    return (
                                        <Button
                                            key={type.key}
                                            type="button"
                                            variant={newParam.type === type.key ? 'default' : 'outline'}
                                            onClick={() => setNewParam({ ...newParam, type: type.key })}
                                        >
                                            <TypeIcon className="mr-2 h-4 w-4" aria-hidden />
                                            {type.text}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="gap-2 pt-4 sm:gap-0">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={closeDialog}
                        >
                            {t('ui.cancel')}
                        </Button>
                        <Button type="button" onClick={addParameter} disabled={!newParam.name?.trim()}>
                            {editParameter ? t('ui.save') : t('ui.add')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
